import {
  JEV_CHOICE_MAX_OPTIONS,
  JEV_SCORE_MAX_LEVELS,
  JEV_SCORE_MIN_LEVELS,
  type JevQuestion
} from "./jev-question.types";
import {
  fieldName,
  isSchemaNode,
  type JevFieldPlan,
  type JevSchemaRefusal,
  type SchemaNode
} from "./jev-schema-plan.types";

/**
 * Anchor levels for a continuous bounded number. Five evenly spaced points, so
 * the probability-weighted score reads back as a value anywhere in the range.
 *
 * debt: a fixed five-anchor rubric for every continuous field; the schema has
 *       no way to name its own levels or their meaning.
 *       Upgrade trigger: a task whose accuracy needs a domain rubric on a
 *       continuous field (it would read an extension keyword for the levels).
 */
const NUMBER_SCALE_LEVELS = 5;

/**
 * Our own floor, not TypeSafe's: one option is not a decision, and a one-value
 * enum is a constant the caller can write without asking a model.
 */
const JEV_CHOICE_MIN_OPTIONS = 2;

/** The node's own non-null type, or a refusal. `["integer", "null"]` reads as integer. */
function singleType(node: SchemaNode, path: readonly string[]): string | JevSchemaRefusal {
  const { type } = node;
  const types = typeof type === "string" ? [type] : Array.isArray(type) ? type : [];
  const nonNull = types.filter((t) => t !== "null");
  if (nonNull.length === 1 && typeof nonNull[0] === "string") return nonNull[0];
  if (nonNull.length === 0) {
    return { field: fieldName(path), reason: "has no type, so there is nothing to ask" };
  }
  return { field: fieldName(path), reason: `mixes types (${nonNull.join(", ")})` };
}

function trimmedText(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

/**
 * How an enclosing object is named inside a nested question: its title, else
 * its description, else its key. The title comes first because it is the
 * short name; a description is a sentence.
 */
export function ancestorLabel(node: SchemaNode, key: string): string {
  return trimmedText(node["title"]) ?? trimmedText(node["description"]) ?? key;
}

/**
 * The question text the model reads. A root-level leaf reads its own
 * description, else title, else key. A nested leaf is prefixed with its
 * enclosing objects and its own key, because Jev never shows the model a
 * field path: without the prefix `shipping.late` and `billing.late` both ask
 * "late".
 */
function instructionsFor(
  node: SchemaNode,
  path: readonly string[],
  ancestors: readonly string[]
): string {
  const key = path[path.length - 1] ?? "(root)";
  const ownText = trimmedText(node["description"]) ?? trimmedText(node["title"]);
  if (ancestors.length === 0) return ownText ?? key;
  const context = [...ancestors, key].join(" › ");
  return ownText === undefined ? context : `${context}: ${ownText}`;
}

function asFiniteNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

export type JevLeafResult =
  | { readonly question: JevQuestion; readonly plan: JevFieldPlan }
  | JevSchemaRefusal;

function translateEnum(
  node: SchemaNode,
  path: readonly string[],
  ancestors: readonly string[],
  values: unknown[]
): JevLeafResult {
  const field = fieldName(path);
  const options = values.filter((v): v is string => typeof v === "string");
  if (options.length !== values.filter((v) => v !== null).length) {
    return { field, reason: "enum values must be strings to become options" };
  }
  if (new Set(options).size !== options.length) {
    return { field, reason: "enum repeats an option" };
  }
  if (options.length < JEV_CHOICE_MIN_OPTIONS) {
    return {
      field,
      reason: `enum needs at least ${JEV_CHOICE_MIN_OPTIONS} options to choose between`
    };
  }
  if (options.length > JEV_CHOICE_MAX_OPTIONS) {
    return {
      field,
      reason: `enum has ${options.length} options; Jev accepts at most ${JEV_CHOICE_MAX_OPTIONS}`
    };
  }
  const criteria = choiceCriteria(node, field, options);
  if ("reason" in criteria) return criteria;
  return {
    question: {
      type: "choice",
      instructions: instructionsFor(node, path, ancestors),
      criteria: criteria.byOption
    },
    plan: { kind: "choice", path, options }
  };
}

/**
 * The schema keyword that says what each option of a string enum means:
 * `{ "enum": ["refund", "repair"], "x-criteria": { "refund": "money back" } }`.
 * An `x-` keyword because JSON Schema has no per-value description, and every
 * validator ignores an `x-` keyword, so the schema stays a valid output contract
 * for a generative model too.
 */
export const X_CRITERIA_KEYWORD = "x-criteria";

function quoted(values: readonly string[]): string {
  return values.map((value) => `"${value}"`).join(", ");
}

/**
 * The choice criteria for an enum: each option's description from
 * {@link X_CRITERIA_KEYWORD}, or null where it gives none. A description that
 * names no option, or is not text, is refused — it would otherwise be dropped
 * and the author would believe the model reads it.
 */
function choiceCriteria(
  node: SchemaNode,
  field: string,
  options: readonly string[]
): { readonly byOption: Readonly<Record<string, string | null>> } | JevSchemaRefusal {
  const raw = node[X_CRITERIA_KEYWORD];
  if (raw === undefined) {
    return { byOption: Object.fromEntries(options.map((option) => [option, null])) };
  }
  if (!isSchemaNode(raw)) {
    return {
      field,
      reason: `${X_CRITERIA_KEYWORD} must be an object that maps an option to what it means`
    };
  }
  const strangers = Object.keys(raw).filter((key) => !options.includes(key));
  if (strangers.length > 0) {
    return {
      field,
      reason: `${X_CRITERIA_KEYWORD} describes ${quoted(strangers)}, which the enum does not offer`
    };
  }
  const notText = options.filter((option) => {
    const value = raw[option];
    return value !== undefined && value !== null && typeof value !== "string";
  });
  if (notText.length > 0) {
    return {
      field,
      reason: `${X_CRITERIA_KEYWORD} for ${quoted(notText)} must be text`
    };
  }
  return {
    byOption: Object.fromEntries(
      options.map((option) => [option, trimmedText(raw[option]) ?? null])
    )
  };
}

function translateInteger(
  node: SchemaNode,
  path: readonly string[],
  ancestors: readonly string[]
): JevLeafResult {
  const field = fieldName(path);
  const minimum =
    asFiniteNumber(node["minimum"]) ??
    ((): number | undefined => {
      const ex = asFiniteNumber(node["exclusiveMinimum"]);
      return ex === undefined ? undefined : Math.floor(ex) + 1;
    })();
  const maximum =
    asFiniteNumber(node["maximum"]) ??
    ((): number | undefined => {
      const ex = asFiniteNumber(node["exclusiveMaximum"]);
      return ex === undefined ? undefined : Math.ceil(ex) - 1;
    })();

  if (minimum === undefined || maximum === undefined) {
    return { field, reason: "an integer needs both a minimum and a maximum to become a scale" };
  }
  const low = Math.ceil(minimum);
  const high = Math.floor(maximum);
  const levels = high - low + 1;
  if (levels < JEV_SCORE_MIN_LEVELS || levels > JEV_SCORE_MAX_LEVELS) {
    return {
      field,
      reason: `an integer from ${low} to ${high} is ${levels} levels; Jev scores ${JEV_SCORE_MIN_LEVELS} to ${JEV_SCORE_MAX_LEVELS}`
    };
  }
  return {
    question: {
      type: "score",
      instructions: instructionsFor(node, path, ancestors),
      criteria: Array.from({ length: levels }, (_, i) => String(low + i))
    },
    plan: { kind: "integer-scale", path, minimum: low, levels }
  };
}

/**
 * Significant digits kept in a number-scale anchor label. A double carries ~15,
 * and the residue of `min + step * i` lives in the last two or three —
 * `1 + 2.7` is `3.6999999999999997`. Twelve keeps every digit a schema author
 * can meaningfully write while shedding that residue.
 */
const ANCHOR_LABEL_SIGNIFICANT_DIGITS = 12;

/**
 * The anchor labels the MODEL reads for a continuous scale: evenly spaced from
 * `minimum` to `maximum`, written as a person would write them. The ends are
 * the bounds themselves rather than computed, so they can never drift.
 *
 * Labels are for the model only. The decoder never parses them back — it maps
 * the answered level INDEX onto `[minimum, maximum]` from the field plan — so a
 * label's formatting can change without changing any decoded value.
 */
export function numberScaleLabels(minimum: number, maximum: number): string[] {
  const last = NUMBER_SCALE_LEVELS - 1;
  const step = (maximum - minimum) / last;
  return Array.from({ length: NUMBER_SCALE_LEVELS }, (_, i) => {
    if (i === 0) return String(minimum);
    if (i === last) return String(maximum);
    return String(Number((minimum + step * i).toPrecision(ANCHOR_LABEL_SIGNIFICANT_DIGITS)));
  });
}

function translateNumber(
  node: SchemaNode,
  path: readonly string[],
  ancestors: readonly string[]
): JevLeafResult {
  const field = fieldName(path);
  const minimum = asFiniteNumber(node["minimum"]);
  const maximum = asFiniteNumber(node["maximum"]);
  if (minimum === undefined || maximum === undefined || maximum <= minimum) {
    return {
      field,
      reason: "a number needs an inclusive minimum below an inclusive maximum to become a scale"
    };
  }
  return {
    question: {
      type: "score",
      instructions: instructionsFor(node, path, ancestors),
      criteria: numberScaleLabels(minimum, maximum)
    },
    plan: { kind: "number-scale", path, minimum, maximum, levels: NUMBER_SCALE_LEVELS }
  };
}

/** Keys that hold reusable definitions for `$ref`, not a field to ask. */
const DEFINITIONS_KEYS: ReadonlySet<string> = new Set(["$defs", "definitions"]);

/**
 * The refusal for a node whose shape is not a value at all — a `$ref`, a
 * definitions block, or a combinator — or `undefined` when it is a value.
 */
function compositionRefusal(
  node: SchemaNode,
  path: readonly string[],
  field: string
): JevSchemaRefusal | undefined {
  if ("$ref" in node) return { field, reason: "uses $ref; inline the referenced schema" };
  const key = path[path.length - 1];
  if (key !== undefined && DEFINITIONS_KEYS.has(key) && !("type" in node)) {
    return {
      field,
      reason: `is a ${key} block for $ref; inline the referenced schema where it is used`
    };
  }
  const combinator = ["anyOf", "oneOf", "allOf", "not"].find((name) => name in node);
  if (combinator !== undefined) {
    return { field, reason: `uses ${combinator}, which has no Jev question` };
  }
  return undefined;
}

/**
 * One schema node as one Jev question, a refusal naming the field, or
 * `"object"` when the node is a nested object the caller should descend into.
 * `ancestors` holds the label of each enclosing object, outermost first
 * (`ancestorLabel`); it is empty for a root-level field.
 */
export function translateJevLeaf(
  node: SchemaNode,
  path: readonly string[],
  ancestors: readonly string[]
): JevLeafResult | "object" {
  const field = fieldName(path);
  const refusal = compositionRefusal(node, path, field);
  if (refusal !== undefined) return refusal;
  if (Array.isArray(node["enum"])) return translateEnum(node, path, ancestors, node["enum"]);
  if ("const" in node) return { field, reason: "is a constant; there is nothing to ask" };

  const type = singleType(node, path);
  if (typeof type !== "string") return type;

  switch (type) {
    case "boolean":
      return {
        question: { type: "noul", instructions: instructionsFor(node, path, ancestors) },
        plan: { kind: "boolean", path }
      };
    case "integer":
      return translateInteger(node, path, ancestors);
    case "number":
      return translateNumber(node, path, ancestors);
    case "object":
      return "object";
    case "string":
      return {
        field,
        reason: "is free text; Jev only answers with an option, a yes/no or a level"
      };
    case "array":
      return {
        field,
        reason:
          "is a list; Jev answers one value per question — use an object with fixed keys instead"
      };
    default:
      return { field, reason: `has type "${type}", which has no Jev question` };
  }
}
